import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import {
  queryOptions,
  useIsFetching,
  useIsMutating,
  useMutation,
  useQueries,
  useQueryClient,
} from '@tanstack/vue-query';
import type { Conversation, ConversationMessage } from '@cutr/constants/order';
import type { ConversationLabel } from '@cutr/constants/conversations';
import {
  createConversation as createConversationRequest,
  deleteConversation as deleteConversationRequest,
  getConversation,
  getOrderConversations,
  markConversationViewed,
  sendConversationMessage,
  setMessageLabels,
  type ConversationDetail,
  type ConversationRow,
  type ConversationSummary,
  type CreateConversationPayload,
} from '@/api/conversations';
import { useToast } from '@/composables/plugins';
import { toastMessageFromAxiosError } from '@/helpers/api';
import { conversationKeys, conversationMutationKeys } from './keys';
import { patchRow } from './shared/listUpdaters';
import { CONVERSATION_STALE_TIME_MS } from './shared/staleTimes';
import { useInvalidateScoped } from './shared/useInvalidateScoped';
import { useScopedQuery } from './shared/useScopedQuery';
import type { MaybeId, QueryFlags } from './shared/types';

export const DIRECT_CONVERSATION_ID = 'direct';

export function isRealConversationId(
  id: string | null | undefined
): id is string {
  return Boolean(id) && id !== DIRECT_CONVERSATION_ID;
}

export function conversationDetailOptions(
  orderId: string,
  conversationId: string
) {
  return queryOptions({
    queryKey: conversationKeys.detail(orderId, conversationId),
    queryFn: ({ signal }) =>
      getConversation(conversationId, { includeMessages: true }, signal),
    staleTime: CONVERSATION_STALE_TIME_MS,
  });
}

export function conversationSummaryOptions(
  orderId: string,
  conversationId: string
) {
  return queryOptions({
    queryKey: conversationKeys.summary(orderId, conversationId),
    queryFn: ({ signal }) =>
      getConversation(conversationId, { includeMessages: false }, signal),
    staleTime: CONVERSATION_STALE_TIME_MS,
  });
}

export function useConversationQuery(
  orderId: MaybeId,
  conversationId: MaybeId
) {
  const order = computed(() => toValue(orderId) ?? '');
  const query = useScopedQuery(
    conversationId,
    (id) => conversationDetailOptions(order.value, id),
    {
      enabled: () =>
        Boolean(order.value) && isRealConversationId(toValue(conversationId)),
    }
  );

  return {
    conversation: computed<ConversationDetail | undefined>(
      () => query.data.value
    ),
    query,
  };
}

export type ConversationSummaryTarget = {
  orderId: string;
  conversationId: string;
};

export function useConversationSummaries(
  targets: MaybeRefOrGetter<ConversationSummaryTarget[]>
) {
  const list = computed(() => toValue(targets));

  return useQueries({
    queries: computed(() =>
      list.value.map(({ orderId, conversationId }) =>
        conversationSummaryOptions(orderId, conversationId)
      )
    ),
    combine: (results) => ({
      byOrderId: Object.fromEntries(
        results.flatMap((result, index) =>
          result.data ? [[list.value[index].orderId, result.data]] : []
        )
      ) as Record<string, ConversationSummary>,
      isLoading: results.some((result) => result.isLoading),
    }),
  });
}

export function orderConversationsOptions(orderId: string) {
  return queryOptions({
    queryKey: conversationKeys.list(orderId),
    queryFn: ({ signal }) => getOrderConversations(orderId, signal),
  });
}

export function useOrderConversationsQuery(
  orderId: MaybeId,
  flags?: QueryFlags
) {
  const query = useScopedQuery(orderId, orderConversationsOptions, flags);

  return { rows: computed(() => query.data.value ?? []), query };
}

export function useConversationDetailsQuery(
  orderId: MaybeId,
  conversationIds: MaybeRefOrGetter<string[]>
) {
  const order = computed(() => toValue(orderId) ?? '');
  const ids = computed(() =>
    order.value ? toValue(conversationIds).filter(isRealConversationId) : []
  );
  const queries = computed(() =>
    ids.value.map((id) => conversationDetailOptions(order.value, id))
  );

  return useQueries({
    queries,
    combine: (results) => ({
      byId: Object.fromEntries(
        results.flatMap((result, index) =>
          result.data ? [[ids.value[index], result.data]] : []
        )
      ) as Record<string, ConversationDetail>,
      updatedAtById: Object.fromEntries(
        results.map((result, index) => [ids.value[index], result.dataUpdatedAt])
      ) as Record<string, number>,
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
    }),
  });
}

const EMPTY_DETAIL = {
  messages: [],
  audits: [],
  calls: [],
  statusEvents: [],
} satisfies Pick<
  Conversation,
  'messages' | 'audits' | 'calls' | 'statusEvents'
>;

export function mergeConversationDetails(
  rows: ConversationRow[],
  byId: Record<string, ConversationDetail>
): Conversation[] {
  return rows.map((row) => ({ ...EMPTY_DETAIL, ...row, ...byId[row.id] }));
}

export function withDirectPlaceholder(
  rows: ConversationRow[],
  {
    isAdmin,
    makerId,
    customerOrganisationId,
  }: {
    isAdmin: boolean;
    makerId?: string | null;
    customerOrganisationId?: string;
  }
): ConversationRow[] {
  if (!isAdmin || !makerId) return rows;
  if (rows.some((row) => row.customerOrganisationId && row.makerOrganisationId))
    return rows;

  const now = new Date().toISOString();
  return [
    {
      id: DIRECT_CONVERSATION_ID,
      createdAt: now,
      updatedAt: now,
      makerOrganisationId: makerId,
      customerOrganisationId,
      count: 0,
      resolvedAt: null,
      isResolved: false,
    } as ConversationRow,
    ...rows,
  ];
}

// True only once the list has seen a message the rendered thread has not, so a
// cold load of an unread conversation does not raise the banner.
export function hasIncomingFor(
  row: Pick<ConversationRow, 'hasUnreadMessages'> | undefined,
  detailUpdatedAt: number,
  listUpdatedAt: number
) {
  if (!row?.hasUnreadMessages || !detailUpdatedAt) return false;
  return listUpdatedAt > detailUpdatedAt;
}

export type SendMessageVariables = {
  orderId: string;
  conversationId: string;
  data: FormData;
};

export type ConversationTarget = { orderId: string; conversationId: string };

export type SetLabelsVariables = ConversationTarget & {
  providerMessageId: ConversationMessage['providerId'];
  labels: ConversationLabel[];
};

export function useInvalidateConversationList() {
  return useInvalidateScoped(conversationKeys.list);
}

export function useInvalidateConversation() {
  const queryClient = useQueryClient();

  return ({ orderId, conversationId }: ConversationTarget) =>
    queryClient.invalidateQueries({
      queryKey: conversationKeys.detail(orderId, conversationId),
    });
}

export function useConversationMutations() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const invalidateList = useInvalidateConversationList();
  const invalidateDetail = useInvalidateConversation();

  const send = useMutation({
    mutationKey: conversationMutationKeys.sendMessage,
    mutationFn: ({ conversationId, data }: SendMessageVariables) =>
      sendConversationMessage(conversationId, data),
    onSuccess: (_row, { orderId, conversationId }) =>
      Promise.all([
        invalidateDetail({ orderId, conversationId }),
        invalidateList(orderId),
      ]),
  });

  const markViewed = useMutation({
    mutationKey: conversationMutationKeys.markViewed,
    mutationFn: ({ conversationId }: ConversationTarget) =>
      markConversationViewed(conversationId),
    onSuccess: (_data, { orderId, conversationId }) =>
      queryClient.setQueryData<ConversationRow[]>(
        conversationKeys.list(orderId),
        patchRow<ConversationRow>({
          id: conversationId,
          hasUnreadMessages: false,
        })
      ),
    onError: (error) => console.error(error),
  });

  // Refetching here would re-identify the thread's messages and bounce the
  // reader to the bottom of the timeline, so the cache is patched instead.
  const setLabels = useMutation({
    mutationKey: conversationMutationKeys.setLabels,
    mutationFn: ({
      conversationId,
      providerMessageId,
      labels,
    }: SetLabelsVariables) =>
      setMessageLabels(conversationId, { providerMessageId, labels }),
    onMutate: async ({
      orderId,
      conversationId,
      providerMessageId,
      labels,
    }) => {
      const queryKey = conversationKeys.detail(orderId, conversationId);
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData<ConversationDetail>(
        queryKey,
        (detail) =>
          detail && {
            ...detail,
            messages: detail.messages.map((message) =>
              message.providerId === providerMessageId
                ? { ...message, labels: labels ?? [] }
                : message
            ),
          }
      );
    },
    // Two label writes can overlap, so reconcile rather than restore.
    onError: (error, variables) => {
      toast.error(toastMessageFromAxiosError(error));
      return invalidateDetail(variables);
    },
  });

  const create = useMutation({
    mutationKey: conversationMutationKeys.create,
    mutationFn: (payload: CreateConversationPayload) =>
      createConversationRequest(payload),
    onSuccess: (_created, { orderId }) => invalidateList(orderId),
    onError: (error) => toast.error(toastMessageFromAxiosError(error)),
  });

  const remove = useMutation({
    mutationKey: conversationMutationKeys.remove,
    mutationFn: ({ conversationId }: ConversationTarget) =>
      deleteConversationRequest(conversationId),
    onSuccess: (_data, { orderId, conversationId }) => {
      queryClient.removeQueries({
        queryKey: conversationKeys.detail(orderId, conversationId),
      });
      queryClient.removeQueries({
        queryKey: conversationKeys.summary(orderId, conversationId),
      });
      return invalidateList(orderId);
    },
    onError: (error) => toast.error(toastMessageFromAxiosError(error)),
  });

  return {
    sendMessage: (variables: SendMessageVariables) =>
      send.mutateAsync(variables),
    markConversationViewed: (variables: ConversationTarget) =>
      markViewed.mutateAsync(variables),
    setMessageLabels: (variables: SetLabelsVariables) =>
      setLabels.mutateAsync(variables).catch(() => undefined),
    createConversation: (payload: CreateConversationPayload) =>
      create.mutateAsync(payload).catch(() => undefined),
    deleteConversation: (variables: ConversationTarget) =>
      remove.mutateAsync(variables).catch(() => undefined),
    isSendingMessage: computed(() => send.isPending.value),
  };
}

export function useConversationActivity(orderId: MaybeId) {
  const order = computed(() => toValue(orderId) ?? '');

  const fetching = useIsFetching({
    queryKey: computed(() => conversationKeys.all(order.value)),
  });
  const sending = useIsMutating({
    mutationKey: conversationMutationKeys.sendMessage,
  });

  return {
    isBusy: computed(
      () => Boolean(order.value) && fetching.value + sending.value > 0
    ),
  };
}
